import React from 'react';
import { Link } from 'react-router-dom';
import { LocalizedCurrency, LocalizedDate } from '../localization/LocalizedComponents';

interface CommissionCardProps {
  commission: {
    id: string;
    title: string;
    description: string;
    amount: number;
    currency: string;
    status: string;
    deadline?: string;
    createdAt: string;
    patron?: {
      username: string;
      avatar: string | null;
    };
    creator?: {
      username: string;
      avatar: string | null;
    };
  };
  viewAs?: 'client' | 'creator';
}

export const CommissionCard: React.FC<CommissionCardProps> = ({ commission, viewAs = 'client' }) => {
  const getStatusInfo = (status: string) => {
    const statusMap = {
      PENDING: { label: 'Pending', class: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
      ACCEPTED: { label: 'Accepted', class: 'bg-blue-100 text-blue-800', icon: '✓' },
      REJECTED: { label: 'Rejected', class: 'bg-red-100 text-red-800', icon: '✗' },
      IN_PROGRESS: { label: 'In Progress', class: 'bg-purple-100 text-purple-800', icon: '⚡' },
      COMPLETED: { label: 'Completed', class: 'bg-green-100 text-green-800', icon: '✓' },
      DELIVERED: { label: 'Delivered', class: 'bg-teal-100 text-teal-800', icon: '📦' },
      APPROVED: { label: 'Approved', class: 'bg-green-100 text-green-800', icon: '✓✓' },
      DISPUTED: { label: 'Disputed', class: 'bg-orange-100 text-orange-800', icon: '⚠️' },
      CANCELLED: { label: 'Cancelled', class: 'bg-gray-100 text-gray-800', icon: '✗' },
      REFUNDED: { label: 'Refunded', class: 'bg-indigo-100 text-indigo-800', icon: '↩️' },
    };

    return statusMap[status as keyof typeof statusMap] || statusMap.PENDING;
  };

  const statusInfo = getStatusInfo(commission.status);
  const otherUser = viewAs === 'client' ? commission.creator : commission.patron;

  return (
    <Link
      to={`/commissions/${commission.id}`}
      className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-pink-600 px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold text-lg truncate flex-1 mr-2">{commission.title}</h3>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusInfo.class}`}>
            <span className="mr-1">{statusInfo.icon}</span>
            {statusInfo.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Other User Info */}
        {otherUser && (
          <div className="flex items-center space-x-2">
            {otherUser.avatar && (
              <img
                src={otherUser.avatar}
                alt={otherUser.username}
                className="w-8 h-8 rounded-full"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-600">
                {viewAs === 'client' ? 'Creator' : 'Client'}
              </p>
              <p className="text-sm font-medium text-gray-900 truncate">
                {otherUser.username}
              </p>
            </div>
          </div>
        )}

        {/* Description Preview */}
        <p className="text-sm text-gray-600 line-clamp-2">{commission.description}</p>

        {/* Amount */}
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-sm text-gray-600">Budget:</span>
          <span className="text-lg font-bold text-orange-600">
            <LocalizedCurrency amount={commission.amount} currency={commission.currency} />
          </span>
        </div>

        {/* Deadline */}
        {commission.deadline && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Deadline:</span>
            <span className="text-gray-900">
              <LocalizedDate date={commission.deadline} format="short" />
            </span>
          </div>
        )}

        {/* Created Date */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Requested:</span>
          <span className="text-gray-900">
            <LocalizedDate date={commission.createdAt} format="short" />
          </span>
        </div>
      </div>

      {/* Footer - Action Hint */}
      <div className="bg-gray-50 px-4 py-2 border-t">
        <p className="text-xs text-gray-600 text-center">
          {commission.status === 'PENDING' && viewAs === 'creator' && 'Click to review and respond'}
          {commission.status === 'PENDING' && viewAs === 'client' && 'Awaiting creator response'}
          {commission.status === 'IN_PROGRESS' && 'Work in progress'}
          {commission.status === 'DELIVERED' && viewAs === 'client' && 'Click to review deliverables'}
          {commission.status === 'COMPLETED' && 'Commission complete'}
          {!['PENDING', 'IN_PROGRESS', 'DELIVERED', 'COMPLETED'].includes(commission.status) && 'Click to view details'}
        </p>
      </div>
    </Link>
  );
};
